import { httpClient } from '@/modules/common/services/http/http-client';
import {
  IInvestmentDetails,
  IInvestmentAmount,
  IRiskSignature,
  IInvestmentReview,
  ITermSignature,
  IInvestmentConclusion,
  IStructuredOperationsSelection,
  IProductInfoResponse
} from './product-investment-types';

export const http = httpClient.withOptions({
  baseURL: `${process.env.VUE_APP_BFF_URL}/instruments`
});

export const httpProducts = httpClient.withOptions({
  baseURL: `${process.env.VUE_APP_BFF_URL}/products`
});

export const httpTransactions = httpClient.withOptions({
  baseURL: `${process.env.VUE_APP_BFF_URL}/transactions`
});

export const httpBffInvestments = httpClient.withOptions({
  baseURL: process.env.VUE_APP_BFF_INVESTMENTS_URL
});

export function getProductSelection(
  productId: string,
  category: string
): Promise<IStructuredOperationsSelection> {
  return httpProducts.get(`/v1/categories/${category}/${productId}/selection`);
}

export function getProductInfo(
  productId: string,
  category: string,
  amount?: number,
  useBaseAsset?: boolean
): Promise<IProductInfoResponse> {
  return httpProducts.get(`/v1/categories/${productId}/${category}/info`, {
    params: { amount, useBaseAsset }
  });
}

export function getInvestmentProductDetails(
  productId: string,
  instrumentType: string
): Promise<IInvestmentDetails> {
  return httpTransactions.get(
    `/v1/investment/${instrumentType}/${productId}/info`
  );
}

export function getInvestmentDetails(
  productId: string,
  instrumentType: string
): Promise<IInvestmentDetails> {
  return http.get(`/v1/investments/instrument/${productId}/info`, {
    params: { instrumentType }
  });
}

export function getInvestmentAmount(
  productId: string,
  amount: number
): Promise<IInvestmentAmount> {
  return http.post(`/v1/investments/instrument/amount`, {
    amount,
    productId
  });
}

export function getProductInvestmentAmount(
  productId: string,
  amount: number
): Promise<IInvestmentAmount> {
  return httpTransactions.post(`/v1/investment/product/amount`, {
    amount,
    productId
  });
}

export function getProductInvestmentReview(
  productId: string,
  value: number,
  instrumentType: string
): Promise<IInvestmentReview> {
  return httpTransactions.get(
    `/v1/investment/product/${instrumentType}/${productId}/review`,
    {
      params: { value }
    }
  );
}

export function getInvestmentReview(
  productId: string,
  amount: number,
  instrumentType: string
): Promise<IInvestmentReview> {
  return http.get(`/v1/investments/instrument/${productId}/review`, {
    params: { amount, instrumentType }
  });
}

export function getProductInvestmentReviewByCategory(
  productId: string,
  amount: number,
  category: string,
  useBaseAsset?: boolean
): Promise<IInvestmentReview> {
  return httpProducts.get(`/v1/categories/${productId}/${category}/review`, {
    params: { amount, useBaseAsset }
  });
}

export function makeInvestment(
  productId: string,
  amount: number
): Promise<IInvestmentConclusion> {
  return http.post(`/v1/investments/instrument`, {
    productId,
    amount
  });
}

export function makeProductInvestment(
  productId: string,
  amount: number
): Promise<IInvestmentConclusion> {
  return httpTransactions.post(`/v1/investment/product`, {
    productId,
    amount
  });
}

export function makeProductInvestmentByCategory(
  productId: string,
  amount: number,
  quantity: number,
  category: string,
  partnerApiId?: string,
  useBaseAsset?: boolean
): Promise<IInvestmentConclusion> {
  return httpProducts.post(`/v1/categories/${productId}/${category}/invest`, {
    amount,
    quantity,
    partnerApiId,
    useBaseAsset
  });
}

export function getPendingRiskSignature(
  productId: string,
  instrumentType: string
): Promise<IRiskSignature> {
  return http.get(`/v1/risks/pending-signatures`, {
    params: { productIds: [productId], instrumentType }
  });
}

export function getProductPendingRiskSignature(
  productId: string,
  category: string
): Promise<IRiskSignature> {
  return httpProducts.get(`/v1/risks/${category}/pending-signatures`, {
    params: { productIds: [productId] }
  });
}

export function getPendingTermSignatureById(
  productId: string,
  instrumentType: string
): Promise<ITermSignature> {
  return httpProducts.get(`/v1/terms/product/${productId}/pending-signatures`, {
    params: { instrumentType }
  });
}

export function riskSignature(instrumentId: string): Promise<void> {
  return httpBffInvestments.post('/v1/riskSignature', {
    instrumentIds: [instrumentId]
  });
}

export function riskProductSignature(instrumentId: string): Promise<void> {
  return httpProducts.post(`/v1/risks/signature-risk`, undefined, {
    params: {
      instrumentId
    }
  });
}

export function termSignature(
  instrumentId: string,
  instrumentType?: string
): Promise<void> {
  return httpProducts.post('/v1/terms/signature', {
    instrumentId,
    instrumentType
  });
}
